import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { InfoCircleOutlined, LockOutlined, MailOutlined, UserOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Col, Form, Input, Layout, Row, Space, Tooltip, Typography } from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";
import { AppLogo } from "@/Assets/Index";
import PhoneInputWithCountryCode, { getPhoneDigits } from "@/Components/Common/PhoneInputWithCountryCode";
import { useAuth } from "@/Context/AuthContext";
import { UserContext } from "@/Context/UserContext";
import { setLocalStorageItem } from "@/Utils/Index";

const { Text, Title } = Typography;
const { Content, Header } = Layout;

export const SignUp = ({ onClose }) => {
  const fetch = useAuthenticatedFetch();
  const { updateProfileInfo } = useContext(UserContext);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Get returnUrl from query params for redirect after signup
  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get("returnUrl");

  // Check for existing authentication on component mount
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    const authToken = localStorage.getItem("authorization");
    if (userId && authToken) {
      const redirectTo = returnUrl ? decodeURIComponent(returnUrl) : "/app/dashboard";
      navigate(redirectTo);
    }
  }, [navigate, returnUrl]);

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    mobile: { countryCode: "+91", phoneNumber: "", fullNumber: "" },
    password: "",
    confirmPassword: "",
  };

  const handleSubmit = async (formData) => {
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const { confirmPassword, mobile, ...restData } = formData;
      // Extract full phone number from mobile object
      const submitData = {
        ...restData,
        mobile: mobile?.fullNumber || "",
      };

      const res = await fetch.post("/signup", submitData);

      if (res.success) {
        const loginPayload = {
          email: submitData.email,
          password: submitData.password,
        };
        const loginData = await fetch.post("/login", loginPayload);

        if (loginData.data) {
          updateProfileInfo(loginData.data);
          setLocalStorageItem("authorization", loginData.data.token);
          setLocalStorageItem("userId", loginData.data._id);

          await login();

          const redirectTo = returnUrl ? decodeURIComponent(returnUrl) : "/app/dashboard";
          navigate(redirectTo);
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Signup failed. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const navigateToLogin = () => {
    if (onClose) {
      onClose();
    }
    const loginUrl = returnUrl ? `/app/login?returnUrl=${returnUrl}` : "/app/login";
    navigate(loginUrl);
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f5f5", margin: 0, padding: 0 }}>
      <style>{`
        body { margin: 0 !important; padding: 0 !important; }
        .ant-layout { margin: 0 !important; padding: 0 !important; }
      `}</style>
      <Header
        style={{
          background: "#fff",
          padding: "0 24px",
          height: "64px",
          display: "flex",
          alignItems: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <img src={AppLogo} alt="Upresume.ai" style={{ height: "40px" }} />
      </Header>
      <Content
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "calc(100vh - 64px)",
          padding: "24px",
          margin: 0,
        }}
      >
        <div style={{ width: "100%", maxWidth: "600px" }}>
          <Card style={{ borderRadius: "12px", boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}>
            <Space direction="vertical" size="medium" style={{ width: "100%" }}>
              <div style={{ textAlign: "center" }}>
                <Title level={2} style={{ color: "#1f2937", marginBottom: "8px" }}>
                  Create Account
                </Title>
              </div>

              {error && (
                <Alert
                  message={error}
                  type="error"
                  showIcon
                  closable
                  onClose={() => setError("")}
                  style={{ marginBottom: "16px" }}
                />
              )}

              <Form
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={initialValues}
                size="large"
                requiredMark={(label, { required }) => (
                  <>
                    {label}
                    {required && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
                  </>
                )}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="First Name"
                      name="firstName"
                      validateTrigger="onBlur"
                      rules={[
                        { required: true, message: "First Name is required" },
                        { min: 3, message: "First Name must be at least 3 characters" },
                        { max: 50, message: "First Name cannot exceed 50 characters" },
                        {
                          pattern: /^[a-zA-Z\s]+$/,
                          message: "First Name can only contain letters",
                        },
                        {
                          validator: (_, value) => {
                            if (value && value.trim() !== value) {
                              return Promise.reject(new Error("First Name cannot start or end with spaces"));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input prefix={<UserOutlined />} placeholder="Enter Your First Name" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Last Name (Optional)"
                      name="lastName"
                      validateTrigger="onBlur"
                      rules={[
                        {
                          pattern: /^[a-zA-Z\s]*$/,
                          message: "Last Name can only contain letters",
                        },
                      ]}
                    >
                      <Input prefix={<UserOutlined />} placeholder="Enter Your Last Name" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Email"
                      name="email"
                      validateTrigger="onBlur"
                      rules={[
                        { required: true, message: "Email is required" },
                        { type: "email", message: "Invalid email" },
                      ]}
                    >
                      <Input prefix={<MailOutlined />} placeholder="Enter Your Email ID" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Phone Number (Optional)"
                      name="mobile"
                      validateTrigger="onBlur"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (!value?.phoneNumber) return Promise.resolve();
                            const requiredDigits = getPhoneDigits(value.countryCode);
                            if (requiredDigits) {
                              if (value.phoneNumber.length !== requiredDigits) {
                                return Promise.reject(new Error(`Phone number must be ${requiredDigits} digits`));
                              }
                            } else if (!/^\d{6,15}$/.test(value.phoneNumber)) {
                              return Promise.reject(new Error("Invalid Mobile Number"));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <PhoneInputWithCountryCode placeholder="Enter Phone Number" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label={
                        <span>
                          Password{" "}
                          <Tooltip
                            title={
                              <div>
                                <div>Password must contain:</div>
                                <ul style={{ margin: "4px 0", paddingLeft: "16px" }}>
                                  <li>At least 8 characters</li>
                                  <li>One uppercase letter (A-Z)</li>
                                  <li>One lowercase letter (a-z)</li>
                                  <li>One number (0-9)</li>
                                </ul>
                              </div>
                            }
                          >
                            <InfoCircleOutlined style={{ color: "#1890ff", cursor: "pointer" }} />
                          </Tooltip>
                        </span>
                      }
                      name="password"
                      validateTrigger="onBlur"
                      rules={[
                        { required: true, message: "Password is required" },
                        { min: 8, message: "Password must be at least 8 characters" },
                        {
                          pattern: /[A-Z]/,
                          message: "Password must contain at least one uppercase letter",
                        },
                        {
                          pattern: /[a-z]/,
                          message: "Password must contain at least one lowercase letter",
                        },
                        {
                          pattern: /\d/,
                          message: "Password must contain at least one number",
                        },
                      ]}
                    >
                      <Input.Password prefix={<LockOutlined />} placeholder="Create Your Password" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Confirm Password"
                      name="confirmPassword"
                      dependencies={["password"]}
                      validateTrigger="onBlur"
                      rules={[
                        { required: true, message: "Confirm Password is required" },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue("password") === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error("Passwords do not match"));
                          },
                        }),
                      ]}
                    >
                      <Input.Password prefix={<LockOutlined />} placeholder="Confirm Your Password" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item style={{ marginBottom: "16px" }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    style={{
                      width: "100%",
                      height: "44px",
                      borderRadius: "8px",
                      borderColor: "#667eea",
                      border: "none",
                    }}
                  >
                    {loading ? "Creating Account..." : "Create Account"}
                  </Button>
                </Form.Item>
              </Form>

              <div style={{ textAlign: "center" }}>
                <Text style={{ color: "#6b7280" }}>
                  Already have an account?{" "}
                  <Button type="link" onClick={navigateToLogin} style={{ padding: 0, fontWeight: "600" }}>
                    Sign in here
                  </Button>
                </Text>
              </div>
            </Space>
          </Card>
        </div>
      </Content>
    </Layout>
  );
};
